import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { LayoutDashboard, Settings2 } from 'lucide-react';
import dayjs from 'dayjs';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartContainer, ChartLegend, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { formatAxis, orderedLegendContent } from '@/components/dashboard/chart-utils';
import { FiscalAccessDenied } from '@/components/fiscal/FiscalAccessDenied';
import type { AppShellContext } from '@/components/layout/AppShell';
import { useFiscalApi } from '@/services/api';
import type { FiscalStatsDto } from '@ip/shared-types';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';

const POLL_MS = 30_000;

function StatTile({ title, value, tone }: { title: string; value: string; tone?: 'destructive' }) {
  return (
    <Card className="py-4">
      <CardContent className="px-4">
        <p className="text-muted-foreground truncate text-xs font-medium" title={title}>
          {title}
        </p>
        <p
          className={cn(
            'mt-1 font-mono text-lg font-semibold tabular-nums',
            tone === 'destructive' && 'text-destructive',
          )}
        >
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <Skeleton className="h-[300px]" />
        <Skeleton className="h-[300px]" />
      </div>
    </div>
  );
}

type ByDayRow = { date: string; confirmed: number; rejected: number };

function DayChart({ title, data }: { title: string; data: ByDayRow[] }) {
  const chartData = data.map((d) => ({
    day: dayjs(d.date).format('DD.MM'),
    confirmed: d.confirmed,
    rejected: d.rejected,
  }));
  const config: ChartConfig = {
    confirmed: { label: t('fiscal.dashboard.confirmed'), color: 'var(--chart-2)' },
    rejected: { label: t('fiscal.dashboard.rejected'), color: 'var(--chart-4)' },
  };
  return (
    <ChartCard title={title}>
      <ChartContainer config={config} className="h-[300px] w-full">
        <BarChart data={chartData}>
          <CartesianGrid vertical={false} strokeOpacity={0.4} />
          <XAxis dataKey="day" tickLine={false} axisLine={false} interval={2} />
          <YAxis tickFormatter={formatAxis} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
          <ChartTooltip content={<ChartTooltipContent />} />
          <ChartLegend content={orderedLegendContent(['confirmed', 'rejected'])} />
          <Bar dataKey="confirmed" stackId="a" fill="var(--color-confirmed)" maxBarSize={16} />
          <Bar dataKey="rejected" stackId="a" fill="var(--color-rejected)" radius={[3, 3, 0, 0]} maxBarSize={16} />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}

/**
 * Fiscal module landing tab: stat tiles (today/month per domain, total
 * rejected in red when > 0, printers online/total) + a 30-day confirmed vs
 * rejected bar chart per domain. Auto-refreshes every 30 s (NotificationBell
 * polling pattern) — silently, no skeleton flash on subsequent ticks.
 * settings.enabled === false (module never configured) short-circuits to an
 * empty state pointing at Settings instead of showing all-zero stats; a 403
 * (no 'fiscal' grant) short-circuits to the shared access-denied card.
 */
export function FiscalDashboard() {
  const { nip } = useOutletContext<AppShellContext>();
  const navigate = useNavigate();
  const { getStats, getSettings } = useFiscalApi();
  const [stats, setStats] = useState<FiscalStatsDto | null>(null);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [settings, statsData] = await Promise.all([getSettings(nip), getStats(nip)]);
      setEnabled(settings.enabled);
      setStats(statsData);
    } catch (error) {
      if ((error as { status?: number })?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(t('errors.loadingFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [nip, getSettings, getStats]);

  useEffect(() => {
    setLoading(true);
    setForbidden(false);
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (forbidden) return <FiscalAccessDenied />;
  if (loading && !stats) return <DashboardSkeleton />;

  if (enabled === false) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <LayoutDashboard className="text-muted-foreground size-5" />
            {t('fiscal.dashboard.notConfiguredTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-3">
          <p className="text-muted-foreground text-sm">{t('fiscal.dashboard.notConfiguredDescription')}</p>
          <Button size="sm" onClick={() => navigate(`/${nip}/fiscal/settings`)}>
            <Settings2 className="size-4" />
            {t('fiscal.dashboard.goToSettings')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const rejectedTotal = stats.invoices.rejected + stats.receipts.rejected;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatTile title={t('fiscal.dashboard.invoicesToday')} value={String(stats.invoices.today)} />
        <StatTile title={t('fiscal.dashboard.invoicesMonth')} value={String(stats.invoices.month)} />
        <StatTile title={t('fiscal.dashboard.receiptsToday')} value={String(stats.receipts.today)} />
        <StatTile title={t('fiscal.dashboard.receiptsMonth')} value={String(stats.receipts.month)} />
        <StatTile
          title={t('fiscal.dashboard.rejectedTotal')}
          value={String(rejectedTotal)}
          tone={rejectedTotal > 0 ? 'destructive' : undefined}
        />
        <StatTile
          title={t('fiscal.dashboard.printersOnline')}
          value={`${stats.printersOnline}/${stats.printersTotal}`}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <DayChart title={t('fiscal.dashboard.chartInvoicesTitle')} data={stats.invoices.byDay} />
        <DayChart title={t('fiscal.dashboard.chartReceiptsTitle')} data={stats.receipts.byDay} />
      </div>
    </div>
  );
}
